import { useCallback, useMemo, useState } from 'react';
import {
  Form,
  FormSizes,
  Field,
  TextInput,
  Button,
  ButtonVariants,
  ButtonSizes,
  useFormState,
  FormValidateOn,
} from '@carboledger/ui';
import { COUNTRY_CODES } from '@/config/countries.constants';
import { buildCountryOptions, isFormDirty, EMPTY_LIST } from '@/lib/utils';
import { ConfirmDialog } from '@/components/feedback';
import {
  AllocationPreferenceField,
  FacilityField,
  TerminalField,
  MaterialField,
  SupplierField,
  DateOfDispatchField,
  DateOfDischargeField,
  TransportNameField,
  GhgEmissionsField,
  GhgEmissionSavingsField,
  RawMaterialTypeField,
  CountryOfOriginField,
  FeedstockCategoryField,
  MultiSiteTransferField as SharedMultiSiteTransferField,
  ReferenceIdField,
  AttachmentsField,
  SummaryCard,
} from '@/components/common';
import { useTranslations } from '@/hooks';
import { ShipmentQuantityFields } from './ShipmentQuantityFields';

import { FACILITY_KINDS, parseFacilityValue } from '@/config/formOptions';
import { shipmentValidation } from '../incomingShipmentsValidation';
import {
  buildMultiSiteTransferOptions,
  MULTI_SITE_TRANSFER,
} from '../constants/incomingShipmentFormOptions';
import {
  buildAllocationPreferenceOptions,
  buildFeedstockCategoryOptions,
} from '@/config/formOptions';
import { buildRawMaterialTypeOptions } from '@/config/rawMaterialTypes.constants';
import styles from './ShipmentForm.module.scss';

// Receive inherits facility, material and supplier from its contract, so it asks for none.
export const MODES = Object.freeze({ add: 'add', edit: 'edit', receive: 'receive' });

// Every field the form owns. A missing key here would make its input uncontrolled.
export const EMPTY_SHIPMENT_FORM = Object.freeze({
  idPrefix: '',
  idNumber: '',
  multiSiteTransfer: MULTI_SITE_TRANSFER.no,
  supplierId: '',
  facilityId: '',
  terminalId: '',
  materialId: '',
  dateOfDispatch: '',
  dateOfDischarge: '',
  vessel: '',
  transportName: '',
  shipmentQuantity: '',
  shipmentQuantityUnit: '',
  // Cross-category conversions only: declaration↔working and declaration↔tracking.
  workingDensity: '',
  trackingUnit: '',
  trackingDensity: '',
  ghgEmissions: '',
  ghgEmissionSavings: '',
  rawMaterialType: '',
  countryOfOrigin: '',
  feedstockCategory: '',
  allocationPreference: [],
  // Edit-only: contracts already linked — chips + ✕ replace the button when non-empty.
  linkedContracts: [],
  attachments: [],
  // Edit-only: files already on the server (view-only) + which of their ids to drop.
  existingAttachments: [],
  removeFileIds: [],
});

/**
 * Props:
 *   title            form header text
 *   submitLabel      primary button text
 *   initialValues    starting values (EMPTY_SHIPMENT_FORM for Add)
 *   onSubmit         (values, helpers) => void — runs only once validation passes
 *   onClose          ✕ and Cancel
 *   isSubmitting     disables the primary button while saving
 *   linkedContracts  the shipment's linked contracts, shown as chips
 *   options          backend-driven lists: { sites, terminals, suppliers, materials,
 *                    units, rawMaterialTypes } — each an Option[]
 *   mode             'add' | 'edit' | 'receive' — edit locks the ID; receive takes its
 *                    facility, material and supplier from the contract instead of asking
 *   locked           edit only: a contract or a declaration is linked — disables
 *                    Received at, Terminal, Material name and Supplier
 *   terminalLocked   receive only: the contract has a terminal, which the shipment must keep
 *   summaryFields    receive only: the contract bar above the fields
 *   material         receive only: the units + fixed density the contract already resolved,
 *                    since there is no Material dropdown to read them from
 *   derivedFrom      receive only: { quantity, unit } the read-only columns convert — the
 *                    contract's amount, so they read as context beside the user's entry
 */
export const ShipmentForm = ({
  title,
  submitLabel,
  initialValues,
  onSubmit,
  onClose,
  isSubmitting = false,
  mode = MODES.add,
  locked = false,
  terminalLocked = false,
  options = {},
  linkedContracts = EMPTY_LIST,
  summaryFields,
  material,
  derivedFrom,
}) => {
  const t = useTranslations();

  const multiSiteTransferOptions = useMemo(() => buildMultiSiteTransferOptions(t), [t]);
  const feedstockCategoryOptions = useMemo(() => buildFeedstockCategoryOptions(t), [t]);
  const allocationPreferenceOptions = useMemo(() => buildAllocationPreferenceOptions(t), [t]);
  const countryOptions = useMemo(() => buildCountryOptions(COUNTRY_CODES), []);
  const rawMaterialTypeOptions = useMemo(() => buildRawMaterialTypeOptions(), []);

  const isReceive = mode === MODES.receive;

  const [confirmingClose, setConfirmingClose] = useState(false);
  const askBeforeClosing = useCallback(() => setConfirmingClose(true), []);
  const keepEditing = useCallback(() => setConfirmingClose(false), []);

  return (
    <Form
      validateOn={FormValidateOn.change}
      size={FormSizes.large}
      initialValues={initialValues}
      onSubmit={onSubmit}
      className={styles.form}
    >
      {({ values, setValue }) => {
        const requestClose = isFormDirty(values, initialValues) ? askBeforeClosing : onClose;

        return (
          <>
            <Form.Header title={title} onClose={requestClose} closeLabel={t('COMMON_CLOSE')} />

            <Form.Section>
              {isReceive ? <SummaryCard fields={summaryFields} columns={6} /> : null}

              <Form.Row columns={4}>
                <div className={isReceive ? undefined : styles.span2}>
                  <ReferenceIdField
                    label={t('FIELD_SHIPMENT_ID')}
                    prefixLabel={t('SHIPMENT_FORM_SHIPMENT_ID_PREFIX')}
                    numberLabel={t('SHIPMENT_FORM_SHIPMENT_ID_NUMBER')}
                    readOnly={mode === MODES.edit}
                    rules={shipmentValidation.idNumber}
                  />
                </div>

                {isReceive ? null : (
                  /* TODO: the update endpoint doesn't persist a changed facility yet. */
                  <FacilityField
                    options={options.facilities}
                    clears={FACILITY_CLEARS}
                    disabled={locked}
                  />
                )}

                {/* Receive has no facility field, but its values still carry the contract's. */}
                <ShipmentTerminalField
                  sites={options.sites}
                  disabled={locked || terminalLocked}
                  clears={isReceive ? undefined : TERMINAL_CLEARS}
                />
              </Form.Row>

              {isReceive ? null : (
                <Form.Row columns={4}>
                  {/* TODO: the update endpoint doesn't persist a changed material yet. */}
                  <div className={styles.span2}>
                    <MaterialField
                      materials={options.materials}
                      certificateType={options.certificateType}
                      clears={MATERIAL_CLEARS}
                      disabled={locked}
                    />
                  </div>

                  <ShipmentMultiSiteTransferField options={multiSiteTransferOptions} />

                  <SupplierField
                    options={options.suppliers}
                    disabled={values.multiSiteTransfer === MULTI_SITE_TRANSFER.yes || locked}
                  />
                </Form.Row>
              )}

              <Form.Row columns={4}>
                <DateOfDispatchField />

                <DateOfDischargeField rules={shipmentValidation.dateOfDischarge} />

                <TransportNameField />

                <Field name="vessel">
                  {({ id, name, value, onChange, onBlur, status, message }) => (
                    <TextInput
                      id={id}
                      name={name}
                      label={t('FORM_VESSEL_NAME')}
                      placeholder={t('FORM_VESSEL_NAME_PLACEHOLDER')}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      status={status}
                      message={message}
                    />
                  )}
                </Field>
              </Form.Row>
            </Form.Section>

            <ShipmentQuantityFields
              linkedContracts={linkedContracts}
              materials={options.materials}
              material={material}
              derivedFrom={derivedFrom}
              values={values}
              setValue={setValue}
            />

            <Form.Section title={t('SECTION_SUSTAINABILITY')}>
              <Form.Row columns={4}>
                <GhgEmissionsField rules={shipmentValidation.ghgEmissions} />

                <GhgEmissionSavingsField rules={shipmentValidation.ghgEmissionSavings} />

                <RawMaterialTypeField options={rawMaterialTypeOptions} />

                <CountryOfOriginField options={countryOptions} />
              </Form.Row>

              <Form.Row columns={4}>
                <FeedstockCategoryField options={feedstockCategoryOptions} />

                <AllocationPreferenceField options={allocationPreferenceOptions} />
              </Form.Row>
            </Form.Section>

            <Form.Section title={t('SECTION_ATTACHMENTS')}>
              <Form.Row columns={1}>
                <AttachmentsField />
              </Form.Row>
            </Form.Section>

            <Form.Footer>
              <Button variant={ButtonVariants.neutral} size={ButtonSizes.sm} onClick={requestClose}>
                {t('COMMON_CANCEL')}
              </Button>
              <Button
                type="submit"
                variant={ButtonVariants.primary}
                size={ButtonSizes.sm}
                disabled={isSubmitting}
              >
                {submitLabel}
              </Button>
            </Form.Footer>

            <ConfirmDialog
              open={confirmingClose}
              title={t('COMMON_DISCARD_TITLE')}
              bodyTitle={t('COMMON_DISCARD_BODY_TITLE')}
              description={t('COMMON_DISCARD_DESCRIPTION')}
              confirmLabel={t('COMMON_YES')}
              onConfirm={onClose}
              onCancel={keepEditing}
            />
          </>
        );
      }}
    </Form>
  );
};

// Optional; a back-to-back facility has no terminals, but the column stays in the grid.
const ShipmentTerminalField = ({ sites, clears, disabled }) => {
  const t = useTranslations();
  const { values } = useFormState();
  const isBackToBack = parseFacilityValue(values.facilityId).kind === FACILITY_KINDS.backToBack;

  return (
    <TerminalField
      sites={sites}
      clears={clears}
      label={t('FORM_TERMINAL_OPTIONAL')}
      disabled={isBackToBack || disabled}
    />
  );
};

// site → terminal → material → unit. Picking a parent drops every stale descendant.
const FACILITY_CLEARS = Object.freeze(['terminalId', 'materialId', 'shipmentQuantityUnit']);
const TERMINAL_CLEARS = Object.freeze(['materialId', 'shipmentQuantityUnit']);
// A new material means new declaration/working units, so every unit-derived answer.
const MATERIAL_CLEARS = Object.freeze([
  'shipmentQuantityUnit',
  'workingDensity',
  'trackingUnit',
  'trackingDensity',
]);

// TODO: enable once the multi-site-transfer branch is built; a back-to-back facility is always No.
const ShipmentMultiSiteTransferField = ({ options }) => {
  const { values } = useFormState();
  const isBackToBack = parseFacilityValue(values.facilityId).kind === FACILITY_KINDS.backToBack;

  return (
    <SharedMultiSiteTransferField
      options={options}
      value={isBackToBack ? MULTI_SITE_TRANSFER.no : undefined}
    />
  );
};
